/**
 * Google Business Tab Component
 * Manages Google My Business integration setup
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  MapPin, 
  Star, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle,
  Save,
  Globe,
  Search
} from 'lucide-react';
import { SuperAdminOnboardingData, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus, SetupMethod } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface GoogleBusinessTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const GoogleBusinessTab: React.FC<GoogleBusinessTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState({
    google_setup_method: data.google_setup_method || SetupMethod.REMOTE_ACCESS,
    google_review_url: data.google_review_url || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Handle form field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(formData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const changedFields = Object.entries(formData).filter(([field, value]) => {
          const originalValue = data[field as keyof SuperAdminOnboardingData];
          return originalValue !== value;
        });
        
        if (changedFields.length === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
          return;
        }
        
        const updates = changedFields.map(([field, value]) => 
          onFieldUpdate(field as keyof SuperAdminOnboardingData, value)
        );
        
        await Promise.all(updates);
        
        toast({
          title: "Success",
          description: `Updated ${changedFields.length} field(s) successfully`
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save Google My Business information",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Update status
  const handleStatusUpdate = async (status: OnboardingStatus) => {
    try {
      // Save changes first if any
      if (hasChanges) {
        await handleSave();
      }
      
      await onFieldUpdate('google_ordering_status', status);
      
      toast({
        title: "Success",
        description: `Google My Business status updated to ${getStatusDisplayName(status)}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  // Generate search URL for Google Business
  const handleSearchGoogleBusiness = () => {
    const searchQuery = `${data.restaurant_name} ${data.address}`.replace(/\s+/g, '+');
    const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
    window.open(searchUrl, '_blank');
  };

  const isComplete = data.google_ordering_status === OnboardingStatus.COMPLETE;
  const hasBasicInfo = Boolean(data.restaurant_name && data.address);

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Google My Business</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Enable Google ordering and review management
                </p>
              </div>
            </div>
            <Badge variant={getStatusBadgeVariant(data.google_ordering_status || OnboardingStatus.NOT_STARTED)}>
              {getStatusDisplayName(data.google_ordering_status || OnboardingStatus.NOT_STARTED)}
            </Badge>
          </div>
        </CardHeader>
      </Card>


      {/* Setup Method */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Setup Method
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Google My Business Setup Method</Label>
            <Select
              value={formData.google_setup_method}
              onValueChange={(value) => handleFieldChange('google_setup_method', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select setup method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SetupMethod.REMOTE_ACCESS}>Assisted Setup</SelectItem>
                <SelectItem value={SetupMethod.DIY}>Customer Self-Setup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.google_setup_method === SetupMethod.REMOTE_ACCESS ? (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Assisted Setup:</strong> Our team will guide the customer through the Google My Business setup process and configure the ordering integration.
              </p>
            </div>
          ) : (
            <div className="p-4 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200">
                <strong>Customer Self-Setup:</strong> The customer will set up their Google My Business profile and ordering integration themselves using our provided guides.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Business Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Current Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <h4 className="font-medium text-sm mb-1">Restaurant Name</h4>
              <p className="text-sm text-muted-foreground">
                {data.restaurant_name || 'Not specified'}
              </p>
            </div>
            
            <div className="p-3 bg-muted/50 rounded-lg">
              <h4 className="font-medium text-sm mb-1">Business Address</h4>
              <p className="text-sm text-muted-foreground">
                {data.address || 'Not specified'}
              </p>
            </div>
          </div>

          {hasBasicInfo ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSearchGoogleBusiness}>
                <Search className="h-4 w-4 mr-2" />
                Search Google Business
              </Button>
            </div>
          ) : (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/50 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Complete the business details section first to enable Google Business search functionality.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Business Profile URL */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Star className="h-5 w-5" />
            Google Business Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="google_review_url">Google Review Request Link</Label>
            <Input
              id="google_review_url"
              value={formData.google_review_url}
              onChange={(e) => handleFieldChange('google_review_url', e.target.value)}
              placeholder="https://g.page/your-restaurant"
            />
            <p className="text-sm text-muted-foreground">
              Enter the URL of the restaurant's Google My Business profile page.
            </p>
          </div>

          {formData.google_review_url && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" asChild>
                <a href={formData.google_review_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Profile
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Setup Process</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.google_setup_method === SetupMethod.REMOTE_ACCESS ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <h4 className="font-medium">Verify Business Profile</h4>
                  <p className="text-sm text-muted-foreground">
                    Check if the business already has a Google My Business profile or create a new one.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <h4 className="font-medium">Configure Ordering Integration</h4>
                  <p className="text-sm text-muted-foreground">
                    Set up the "Order Online" button and link it to the restaurant's ordering system.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <h4 className="font-medium">Optimize Listing</h4>
                  <p className="text-sm text-muted-foreground">
                    Update business information, add photos, and configure operating hours.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </div>
                <div>
                  <h4 className="font-medium">Test & Monitor</h4>
                  <p className="text-sm text-muted-foreground">
                    Test the ordering flow and set up review monitoring and response management.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <h4 className="font-medium">Claim or Create Profile</h4>
                  <p className="text-sm text-muted-foreground">
                    Customer claims existing Google listing or creates a new Google My Business profile.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <h4 className="font-medium">Verify Business</h4>
                  <p className="text-sm text-muted-foreground">
                    Complete Google's business verification process (usually by phone or postcard).
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <h4 className="font-medium">Add Ordering Link</h4>
                  <p className="text-sm text-muted-foreground">
                    Add the provided ordering system URL to their Google My Business profile.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </div>
                <div>
                  <h4 className="font-medium">Complete Profile</h4>
                  <p className="text-sm text-muted-foreground">
                    Add photos, business hours, and other relevant information to optimize their listing.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            
            {!isComplete && (
              <>
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.AWAITING_CONFIRMATION)}
                >
                  Mark as In Progress
                </Button>
                
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.COMPLETE)}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Complete
                </Button>
              </>
            )}
            
            <Button 
              variant="outline"
              onClick={() => handleStatusUpdate(OnboardingStatus.NEEDS_ATTENTION)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Mark as Needs Attention
            </Button>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Next Steps</h4>
            <p className="text-sm text-muted-foreground">
              {!hasBasicInfo 
                ? "Complete the business details section first to proceed with Google My Business setup."
                : formData.google_setup_method === SetupMethod.REMOTE_ACCESS
                ? "Setup method configured for assisted setup. Our team will help configure the Google My Business integration."
                : "Setup method configured for customer self-setup. Provide the customer with Google My Business setup documentation."
              }
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleBusinessTab;